import { isEmpty } from 'lodash';

/**
 * Update or insert a parent test's agent entry in the agentsByTest array.
 * If an entry for the same parent test already exists, it is replaced.
 * Otherwise the new entry is appended.
 *
 * Ported from portal/ui testsDetails.controller.js createFullAgentsByTest().
 *
 * @param {Array} existingAgentsByTest - mutable array of { test, agentIds, agentsAll } entries
 * @param {object} newAgentsForTest - the new entry to upsert
 */
function createFullAgentsByTest(existingAgentsByTest, newAgentsForTest) {
    const i = existingAgentsByTest.findIndex((entry) => entry.test === newAgentsForTest.test);

    if (i !== -1) {
        existingAgentsByTest[i] = { ...newAgentsForTest };
    } else {
        existingAgentsByTest.push({ ...newAgentsForTest });
    }
}

/**
 * Compute a unified agents object from all agentsByTest entries.
 * If any entry has agentsAll=true, returns { all: true }.
 * Otherwise, collects all unique agent IDs across entries.
 *
 * Ported from portal/ui testsDetails.controller.js uniteAgentsByTest().
 *
 * @param {Array} agentsByTest - array of { test, agentIds, agentsAll } entries
 * @returns {{ all: boolean, ids: string[], tags: string[], matchAllTags: boolean }}
 */
function uniteAgentsByTest(agentsByTest) {
    const allNPoints = { all: true, ids: [], tags: [], matchAllTags: false };

    if (agentsByTest.some((entry) => entry.agentsAll === true)) {
        return allNPoints;
    }

    const fullAgentsList = ['00000000-0000-0000-0000-000000000000'];
    agentsByTest.forEach((entry) => {
        fullAgentsList.push(...(entry.agentIds || []));
    });

    const uniqueAgentsList = [...new Set(fullAgentsList.map((s) => s.trim()))];

    return { all: false, ids: uniqueAgentsList, tags: [], matchAllTags: false };
}

/**
 * Propagate parent wifiConnectMultiTest agent config to selected child subtests.
 * Fire-and-forget: child tests are saved independently without awaiting results,
 * matching portal/ui's behavior in updateChildWithParentAgents().
 *
 * @param {object} parentTest - the parent test data being saved
 * @param {Array} wifiTestList - full list of available WiFi tests (from useWifiTests hook)
 * @param {Array} matchingAgents - agents matched by tag selection (from agent selector)
 * @param {Function} saveChildTest - function that saves a single child test (PUT API call)
 */
export function syncChildTestAgents(parentTest, wifiTestList, matchingAgents, saveChildTest) {
    const selectedSubtestIds = [
        parentTest.testConfig?.subtest_1,
        parentTest.testConfig?.subtest_2,
        parentTest.testConfig?.subtest_3,
        parentTest.testConfig?.subtest_4,
        parentTest.testConfig?.subtest_5,
    ]
        .filter(Boolean)
        .filter((id) => id !== '');

    if (isEmpty(selectedSubtestIds) || !parentTest._id) {
        return;
    }

    const taggedAgentIds = matchingAgents.map((agent) => agent._id);

    const agentsAndParentTest = {
        test: parentTest._id,
        agentIds: parentTest.agents?.ids || [],
        agentsAll: parentTest.agents?.all || false,
    };

    if (!isEmpty(taggedAgentIds)) {
        agentsAndParentTest.agentIds = taggedAgentIds;
    }

    const subtests = (wifiTestList || []).filter((t) => selectedSubtestIds.includes(t._id));

    subtests.forEach((sTest) => {
        const agentsByTest = (sTest.agentsByTest || []).filter(Boolean);

        if (isEmpty(agentsByTest)) {
            sTest.agentsByTest = [agentsAndParentTest];
        } else {
            createFullAgentsByTest(agentsByTest, agentsAndParentTest);
            sTest.agentsByTest = agentsByTest;
        }

        sTest.agents = uniteAgentsByTest(sTest.agentsByTest);

        // Fire-and-forget save matching portal/ui's TestsService.save(sTest, true)
        saveChildTest(sTest);
    });
}
